// src/pages/inventory/StockList.js
import React, { useState, useEffect, Fragment, useRef } from "react";
import { Card, Col, Table, Input, CardBody, Button } from "reactstrap";
import { FormLabel, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Filter, PlusCircle, X } from "react-feather";
import Skeleton from "react-loading-skeleton";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import MyPagination from "../pagination";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { useNavigate } from "react-router-dom";
// import { getStockList } from "./StockService";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import Select from 'react-select';
import { getMinStockAlert } from "./ManageStocksService";
import StockItemTypeTabs from "./StockItemTypeTabs";
import { getStockApiTypeFilters, getStockEmptyMessage, getStockItemTypeLabel, getStockSearchPlaceholder, isMedicineLikeItemType } from "./stockListHelpers";


const MinStockAlert = () => {
	const [stockData, setStockData] = useState([]);
	const [page, setPage] = useState(1);
	const [size, setSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	const [searchValue, setSearchValue] = useState("");
	const [itemType, setItemType] = useState("medicine");
	const [fromDate, setFromDate] = useState('');
	const [toDate, setToDate] = useState('');
	const [supplierList, setSupplierList] = useState([]);
	const [selectedSupplier, setSelectedSupplier] = useState('');
	const { setLoading } = useLoader();
	const [skeletonLoading, setSkeletonLoading] = useState(false);
	const [isClearing, setIsClearing] = useState(false);
	const { t } = useTranslation();
	const { pharmacyPOS } = usePharmacy();
	const history = useNavigate();

	const eyeButtonRefs = useRef({});

	// Tooltips
	const [editTooltipOpen, setEditTooltipOpen] = useState(false);
	const [viewTooltipOpen, setViewTooltipOpen] = useState({});
	const [viewItemsTooltipOpen, setViewItemsTooltipOpen] = useState(false);
	const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
	const toggleEditTooltip = () => setEditTooltipOpen(!editTooltipOpen);
	const toggleViewTooltip = (id) => {
		setViewTooltipOpen((prev) => ({
			...prev,
			[id]: !prev[id],
		}));
	};
	const toggleViewItemsTooltip = () => setViewItemsTooltipOpen(!viewItemsTooltipOpen);
	const toggleDeleteTooltip = () => setDeleteTooltipOpen(!deleteTooltipOpen);

	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
	const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
	const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);

	useEffect(() => {
		getStocks();
	}, [page, size, itemType]);

	const getStocks = async () => {
		const body = {
			clinicId: pharmacyPOS?.hospital_id,
			...getStockApiTypeFilters(itemType),
			page: page,
			limit: size,
			search: searchValue.trim()
		};
		try {
			setLoading(true);
			setSkeletonLoading(true);
			const response = await getMinStockAlert(body);
			const data = response?.data?.data || {};
			setStockData(data.stocks || []);
			setTotalItems(Math.max(1, Math.ceil((data.total ?? 0) / size)));
		} catch (error) {
			console.error("Error fetching sales history:", error);
		} finally {
			setLoading(false);
			setSkeletonLoading(false);
		}
	};

	const handleFromDateChange = (event) => {
		setFromDate(event.target.value);
	};

	const handleToDateChange = (event) => {
		setToDate(event.target.value);
	};

	const handleSearchButtonClick = () => {
		if (searchValue.trim !== "") {
			setPage(1); // Reset page to 1
			getStocks(); // Trigger the API call for search
		}
	};

	const handleClear = () => {
		setIsClearing(true);
		setSearchValue(''); // Clear the search value
		setSelectedSupplier('')
	};

	useEffect(() => {
		if (isClearing) {
			getStocks();
			setIsClearing(false);
		}
	}, [isClearing]);

	const handleItemTypeChange = (type) => {
		setItemType(type);
		setPage(1);
	};

	const handlePageChange = (pageNumber) => setPage(pageNumber);

	const handlePageSizeChange = (e) => {
		setSize(parseFloat(e.target.value));
		setPage(1);
	};

	const [selectedStock, setSelectedStock] = useState(null);

	const handleViewClick = (item) => {
		setSelectedStock(item);
	};

	const formatDate = (dateString) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		const day = date.getDate().toString().padStart(2, '0');
		const month = date.toLocaleString(undefined, { month: 'short' });
		const year = date.getFullYear();
		return `${day} ${month} ${year}`; // Example: May 07, 2025
	};

	return (
		<Fragment>
			<Breadcrumbs
				mainTitle={t("Minimum Stocks")}
				parent={t("Dashboard")}
				title={t("Minimum Stocks")}
			/>
			<Col sm="12">
				<Card>
					<CardBody>
						<StockItemTypeTabs
							itemType={itemType}
							onChange={handleItemTypeChange}
							t={t}
							idPrefix="min-stock"
						/>
						{/* Search & Filter */}
						<div className="row">
							<Col sm="8" lg="8" xl="10" md="8">
								<label className="form-label">{t("Search")}</label>
								<Input
									name="SearchData"
									id="SearchData"
									type="text"
									placeholder={getStockSearchPlaceholder(itemType, t)}
									value={searchValue}
									onChange={(e) => setSearchValue(e.target.value)}
									style={{
										lineHeight: 1.8, fontSize: "1rem !important"
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											handleSearchButtonClick();
										}
									}}
								/>
							</Col>
							<Col className="col-xl-2 col-lg-4 col-md-4 col-sm-4 d-flex justify-content-start align-items-center" style={{ gap: '18px', marginTop: "20px" }}>
								<button id='FilterToolTip' className='btn btn-primary' style={{ height: "40px" }} onClick={handleSearchButtonClick}>
									<Filter />
								</button>
								<ToolTip
									attrToolTip={{
										placement: 'top',
										isOpen: filterTooltipOpen,
										target: 'FilterToolTip',
										toggle: toggleFilterTooltip,
									}} >
									<strong>{t("Filter")}</strong>
								</ToolTip>
								<button id='ClearToolTip' className='btn btn-secondary' style={{ height: "40px" }} onClick={handleClear}>
									<X />
								</button>
								<ToolTip
									attrToolTip={{
										placement: 'top',
										isOpen: clearTooltipOpen,
										target: 'ClearToolTip',
										toggle: toggleClearTooltip,
									}} >
									<strong>{t("Clear")}</strong>
								</ToolTip>
							</Col>
						</div>
					</CardBody>
				</Card>

				<Card>
					{/* Table */}
					<div className="card-block">
						<div className="row" style={{ padding: "12px 24px" }}>
							<Col sm="12">
								<div className="rounded table-responsive">
									<Table className="table mb-0 table-bordered table-hover table-striped">
										<thead>
											<tr className="align-middle">
												<th>#</th>
												<th>{t("Code")}</th>
												{isMedicineLikeItemType(itemType) ? (
													<>
														<th>{t("Brand Name")}</th>
														<th>{t("Salt Name / Composition")}</th>
													</>
												) : (
													<th>{t("Product Name")}</th>
												)}
												<th>{t("Manufacturer")}</th>
												<th>{t("Form")}</th>
												<th>{t("Package")}</th>
												<th>{t("Unit Sale Price")}</th>
												<th>{t("Total Qty")}</th>
												<th>{t("Remaining Qty")}</th>
												<th className="text-center">{t("Action")}</th>
											</tr>
										</thead>
										<tbody>
											{stockData.length > 0 ? (
												stockData.map((item, idx) => {
													// pick expiry status from stockData[0]
													const stock = item.stockData?.[0];
													let expiryStatus = "Valid";
													if (stock?.isExpired) expiryStatus = "Expired";
													else if (stock?.nearToExpires) expiryStatus = "Near Expiry";

													return (
														<tr key={item._id} className="align-middle">
															<td>{(page - 1) * size + idx + 1}</td>
															<td>{item.code}</td>
															{isMedicineLikeItemType(itemType) ? (
																<>
																	<td>{item.brandName}</td>
																	<td>{item.saltName || "-"}</td>
																</>
															) : (
																<td>{item.brandName}</td>
															)}
															<td>{item.manufacturer}</td>
															<td>{item.productForm}</td>
															<td>{item.package}</td>
															<td>{stock?.unitPricePerMedicine || "-"}</td>
															<td>{item.totalMedicineCount}</td>
															<td>{item.totalRemainingMedicine}</td>
															{/* <td>
																<span
																	className={`badge ${expiryStatus === "Expired"
																		? "bg-danger"
																		: expiryStatus === "Near Expiry"
																			? "bg-warning"
																			: "bg-success"
																		}`}
																>
																	{expiryStatus}
																</span>
															</td> */}
															<td className="text-center">
																<button className='success-action-button' id={`ViewToolTip-${item._id}`}
																	ref={(el) =>
																		(eyeButtonRefs.current[item._id] = el)
																	}
																	onClick={() => {
																		if (eyeButtonRefs.current[item._id]) {
																			eyeButtonRefs.current[item._id].blur();
																		}
																		setViewTooltipOpen((prev) => ({
																			...prev,
																			[item._id]: false,
																		}));
																		handleViewClick(item)
																	}}>
																	<i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
																	<ToolTip
																		attrToolTip={{
																			placement: 'top',
																			isOpen: viewTooltipOpen[item._id] || false,
																			target: `ViewToolTip-${item._id}`,
																			toggle: () => toggleViewTooltip(item._id),
																		}} >
																		<strong>{t("View")}</strong>
																	</ToolTip>
																</button>
															</td>
														</tr>
													);
												})
											) : (
												<tr>
													<td colSpan={isMedicineLikeItemType(itemType) ? 11 : 10} className="p-3 text-center text-muted">
														{getStockEmptyMessage(itemType, t)}
													</td>
												</tr>
											)}
										</tbody>
									</Table>
								</div>
							</Col>

							{/* Pagination */}
							<Col sm="12" className="mt-4">
								<div className="text-center d-md-flex align-items-center justify-content-between">
									<div className="pt-3 show-entries">
										<FormLabel>{t("Show")}&nbsp;</FormLabel>
										<select
											value={size}
											onChange={handlePageSizeChange}
											className="form-control"
											style={{ display: "unset", width: "auto" }}
										>
											<option value={5}>5</option>
											<option value={10}>10</option>
											<option value={20}>20</option>
											<option value={30}>30</option>
											<option value={50}>50</option>
										</select>
										&nbsp;
										<FormLabel>{t("entries")}</FormLabel>
									</div>
									<MyPagination
										totalPages={totalItems}
										currentPage={page}
										onPageChange={handlePageChange}
									/>
								</div>
							</Col>
						</div>
					</div>
				</Card>
			</Col>

			<Modal show={!!selectedStock} onHide={() => setSelectedStock(null)} size="lg">
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedStock?.brandName} ({selectedStock?.code})
						{" "}
						<span className="badge bg-info ms-2">
							{getStockItemTypeLabel(selectedStock, t)}
						</span>
					</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					{selectedStock && (
						<div className="table-responsive">
							<Table bordered>
								<tbody>
									{/* Show different labels based on isOTC */}
									{selectedStock.isOTC ? (
										<>
											<tr><th>{t("Product")}</th><td>{selectedStock.brandName}</td></tr>
											<tr><th>{t("Manufacturer")}</th><td>{selectedStock.manufacturer}</td></tr>
											<tr><th>{t("Category")}</th><td>{selectedStock.productForm}</td></tr>
											<tr><th>{t("Package")}</th><td>{selectedStock.package} ({selectedStock.packageQuantity})</td></tr>
											<tr><th>{t("Total Quantity")}</th><td>{selectedStock.totalMedicineCount}</td></tr>
											<tr><th>{t("Remaining Quantity")}</th><td>{selectedStock.totalRemainingMedicine}</td></tr>
										</>
									) : (
										<>
											<tr><th>{t("Brand")}</th><td>{selectedStock.saltName}</td></tr>
											<tr><th>{t("Manufacturer")}</th><td>{selectedStock.manufacturer}</td></tr>
											<tr><th>{t("Form")}</th><td>{selectedStock.productForm}</td></tr>
											<tr><th>{t("Package")}</th><td>{selectedStock.package} ({selectedStock.packageQuantity})</td></tr>
											<tr><th>{t("Total Strips")}</th><td>{selectedStock.totalStripQuantity}</td></tr>
											<tr><th>{t("Total Medicine")}</th><td>{selectedStock.totalMedicineCount}</td></tr>
											<tr><th>{t("Remaining Medicine")}</th><td>{selectedStock.totalRemainingMedicine}</td></tr>
										</>
									)}
								</tbody>
							</Table>

							<h5 className="mb-2 mt-3">{t("Batch Details")}</h5>
							<Table striped bordered>
								<thead>
									<tr>
										<th>{t("Batch No")}</th>
										<th>{t("Manufacturing Date")}</th>
										<th>{t("Expiry Date")}</th>
										<th>{t("Unit Sale Price")}</th>
										<th>{t("Discount")}</th>
										<th>{t("Total Qty")}</th>
										<th>{t("Remaining")}</th>
										<th>{t("Purchase No")}</th>
									</tr>
								</thead>
								<tbody>
									{selectedStock.stockData?.map((s) => (
										<tr key={s._id}>
											<td>{s.batchNumber}</td>
											<td>{formatDate(s.manufacturingDate)}</td>
											<td>{formatDate(s.expiryDate)}</td>
											<td>{s.unitPricePerMedicine}</td>
											<td>{s.discount}</td>
											<td>{s.totalMedicine}</td>
											<td>{s.totalRemainingMedicine}</td>
											<td>{s.purchaseNumber}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					)}
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" onClick={() => setSelectedStock(null)}>
						{t("Close")}
					</Button>
				</Modal.Footer>
			</Modal>

			{/* <Modal show={!!selectedStock} onHide={() => setSelectedStock(null)} size="lg">
				<Modal.Header closeButton>
					<Modal.Title>{selectedStock?.brandName} ({selectedStock?.code})</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{selectedStock && (
						<div className="table-responsive">
							<Table bordered>
								<tbody>
									<tr><th>Salt</th><td>{selectedStock.saltName}</td></tr>
									<tr><th>Manufacturer</th><td>{selectedStock.manufacturer}</td></tr>
									<tr><th>Form</th><td>{selectedStock.productForm}</td></tr>
									<tr><th>Package</th><td>{selectedStock.package}</td></tr>
									<tr><th>Total Strips</th><td>{selectedStock.totalStripQuantity}</td></tr>
									<tr><th>Total Medicine</th><td>{selectedStock.totalMedicineCount}</td></tr>
									<tr><th>Remaining Medicine</th><td>{selectedStock.totalRemainingMedicine}</td></tr>
								</tbody>
							</Table>

							<h5 className="mb-3">{t("Batch Details")}</h5>
							<Table striped bordered>
								<thead>
									<tr>
										<th>Batch No</th>
										<th>Expiry Date</th>
										<th>Unit Sale Price</th>
										<th>Discount</th>
										<th>Total Qty</th>
										<th>Remaining</th>
										<th>Purchase No</th>
									</tr>
								</thead>
								<tbody>
									{selectedStock.stockData?.map((s) => (
										<tr key={s._id}>
											<td>{s.batchNumber}</td>
											<td>{formatDate(s.expiryDate)}</td>
											<td>{s.unitPrice}</td>
											<td>{s.discount}</td>
											<td>{s.totalMedicine}</td>
											<td>{s.totalRemainingMedicine}</td>
											<td>{s.purchaseNumber}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setSelectedStock(null)}>
						Close
					</Button>
				</Modal.Footer>
			</Modal> */}
		</Fragment>
	);
};

export default MinStockAlert;
